import React, { useContext } from 'react';
import { CheckCircle, XCircle, AlertCircle } from 'lucide-react';
import { ThemeContext } from '../context/ThemeProvider';

const AnswerMessage = ({ correct_answer, selected_answer }) => {
    const { theme } = useContext(ThemeContext);
    
    const isMultipleChoice = Array.isArray(correct_answer);
    const cleanedSelectedAnswers = Array.isArray(selected_answer) ? selected_answer.filter(answer => answer !== 'X') : selected_answer;
    const sortedSelectedAnswers = Array.isArray(cleanedSelectedAnswers) ? [...cleanedSelectedAnswers].sort() : cleanedSelectedAnswers;
    const sortedCorrectAnswers = isMultipleChoice ? [...correct_answer].sort() : correct_answer;

    const isCorrectAnswer = () => {
        if (isMultipleChoice) {
            if (sortedCorrectAnswers.length !== sortedSelectedAnswers.length) {
                return false;
            }
            for (let i = 0; i < sortedCorrectAnswers.length; i++) {
                if (sortedCorrectAnswers[i] !== sortedSelectedAnswers[i]) {
                    return false;
                }
            }
            return true;
        } else {
            return correct_answer === selected_answer;
        }
    };

    const didNotAnswer = () => {
        if (Array.isArray(selected_answer)) {
            return selected_answer.length === 1 && selected_answer[0] === 'X';
        } else {
            return selected_answer === 'X';
        }
    };

    // Configuration for different states using new color system
    const getMessageConfig = () => {
        if (didNotAnswer()) {
            return {
                type: 'unanswered',
                icon: AlertCircle,
                title: 'Question Skipped',
                message: 'You did not answer this question.',
                bgColor: theme.dashboard.review.badge.unanswered.background,
                borderColor: theme.dashboard.status.unanswered,
                iconColor: theme.dashboard.status.unanswered,
                titleColor: theme.dashboard.review.badge.unanswered.text,
                messageColor: theme.text.secondary,
                cardBg: theme.dashboard.review.itemBackground,
            };
        } else if (isCorrectAnswer()) {
            return {
                type: 'correct',
                icon: CheckCircle,
                title: 'Correct!',
                message: 'Great job! You got it right.',
                bgColor: theme.dashboard.review.badge.correct.background,
                borderColor: theme.dashboard.status.correct,
                iconColor: theme.dashboard.status.correct,
                titleColor: theme.dashboard.review.badge.correct.text,
                messageColor: theme.text.secondary,
                cardBg: theme.dashboard.review.itemBackground,
            };
        } else {
            return {
                type: 'incorrect',
                icon: XCircle,
                title: 'Not Quite!',
                message: `You selected "${Array.isArray(sortedSelectedAnswers) ? sortedSelectedAnswers.join(', ') : sortedSelectedAnswers}", but the correct answer is "${Array.isArray(sortedCorrectAnswers) ? sortedCorrectAnswers.join(', ') : sortedCorrectAnswers}".`,
                bgColor: theme.dashboard.review.badge.incorrect.background,
                borderColor: theme.dashboard.status.incorrect,
                iconColor: theme.dashboard.status.incorrect,
                titleColor: theme.dashboard.review.badge.incorrect.text,
                messageColor: theme.text.secondary,
                cardBg: theme.dashboard.review.itemBackground,
            };
        }
    };

    const config = getMessageConfig();
    const IconComponent = config.icon;

    const styles = {
        container: {
            display: 'flex',
            alignItems: 'center',
            gap: '1rem',
            padding: '1rem 1.5rem',
            borderRadius: '0.75rem',
            background: theme.dashboard.review.itemBackground,
            marginBottom: '1rem',
            position: 'relative',
            overflow: 'hidden',
        },
        borderAccent: {
            position: 'absolute',
            left: 0,
            top: 0,
            bottom: 0,
            width: '4px',
            background: `linear-gradient(180deg, ${config.borderColor}, ${config.iconColor})`,
            borderRadius: '0 0.25rem 0.25rem 0',
        },
        iconContainer: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '2.5rem',
            height: '2.5rem',
            borderRadius: '50%',
            background: config.bgColor,
            border: `1px solid ${config.borderColor}`,
            flexShrink: 0,
        },
        content: {
            flex: 1,
            minWidth: 0, // Allows text to wrap properly
        },
        title: {
            fontSize: '1rem',
            fontWeight: '600',
            color: config.titleColor,
            marginBottom: '0.25rem',
            lineHeight: '1.2',
        },
        message: {
            fontSize: '0.875rem',
            color: config.messageColor,
            lineHeight: '1.4',
        },
        statusBadge: {
            position: 'absolute',
            top: '0.75rem',
            right: '1rem',
            fontSize: '0.75rem',
            fontWeight: '600',
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            padding: '0.25rem 0.5rem',
            borderRadius: '0.375rem',
            background: config.bgColor,
            color: config.titleColor,
            border: `1px solid ${config.borderColor}`,
        }
    };

    return (
        <div style={styles.container}>
            {/* Left border accent */}
            <div style={styles.borderAccent} />
            
            {/* Icon */}
            <div style={styles.iconContainer}>
                <IconComponent 
                    size={20}
                    style={{ color: config.iconColor }}
                />
            </div>
            
            {/* Content */}
            <div style={styles.content}>
                <div style={styles.title}>
                    {config.title}
                </div>
                <div style={styles.message}>
                    {config.message}
                </div>
            </div>
            
            {/* Status badge */}
            {/* <div style={styles.statusBadge}>
                {config.type}
            </div> */}
        </div>
    );
};

export default AnswerMessage;